import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {
  ProcedureType,
  ProcessClientOrigin,
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
  ProcessSingletonTaskSchemaName,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {makeNewEmptyMethodNode} from 'procedures/design/flowchartManagementUtils';
import {parseMethodNodeFromNetwork} from 'procedures/network/parseProcedureFromNetwork';
import {MethodNodeREST} from 'procedures/network/ProcedureDAO';
import {serializeMethodNodeForNetwork} from 'procedures/network/serializeProcedureFlowchartForNetwork';
import {MethodNode, ProcedureParameterDataTypeDefinition} from 'procedures/ProcedureDomainTypes';
import MethodSidepanelEditor from 'procedures/processes/MethodSidepanelEditor';
import {getIncrementedNodeName} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import {
  NodeCreationContext,
  ProcessFlowchartTaskSchemaNodeAdapter,
  TaskSchemaDisplayOptions,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {
  NodeSettingsTabKeys,
  ProcessTaskDefinitionVersionGraphQL,
  SidepanelTab,
  TaskSchemaNodeSectionType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaNodeRowData,
  TaskSchemaNodeSectionData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import getIconAndLabel from 'registry/field/GetFieldLabelAndIcon';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {SidepanelSize} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import {assertNever} from 'util/Types';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';

/**
 * Field group keys used to categorize procedure fields.
 * Keep in sync with ProcedureFieldGroupKey in
 * benchling/procedures/controller/processes_platform/procedure_method_task_execution_type_adapter.py
 */
export enum ProcedureFieldGroupKey {
  MATERIAL_INPUTS = 'MATERIAL_INPUTS',
  MATERIAL_OUTPUTS = 'MATERIAL_OUTPUTS',
  DATA_PARAMETERS = 'DATA_PARAMETERS',
  EQUIPMENT = 'EQUIPMENT',
}

type ProcedureSectionConfig = Pick<TaskSchemaNodeSectionData, 'sectionName' | 'sectionType'>;

/**
 * Configuration for procedure node sections, defining how each field group
 * should be displayed and categorized in the flowchart node.
 */
const PROCEDURE_SECTION_CONFIGURATION_BY_FIELD_GROUP_KEY: Record<
  ProcedureFieldGroupKey,
  ProcedureSectionConfig
> = {
  [ProcedureFieldGroupKey.MATERIAL_INPUTS]: {
    sectionName: 'Material Inputs',
    sectionType: TaskSchemaNodeSectionType.INPUTS,
  },
  [ProcedureFieldGroupKey.MATERIAL_OUTPUTS]: {
    sectionName: 'Material Outputs',
    sectionType: TaskSchemaNodeSectionType.OUTPUTS,
  },
  [ProcedureFieldGroupKey.DATA_PARAMETERS]: {
    sectionName: 'Parameters',
    sectionType: TaskSchemaNodeSectionType.NOT_MAPPABLE,
  },
  [ProcedureFieldGroupKey.EQUIPMENT]: {
    sectionName: 'Equipment',
    sectionType: TaskSchemaNodeSectionType.NOT_MAPPABLE,
  },
};

interface ProcedureFieldInfo {
  cid: string;
  name: string;
  dataType: ProcedureParameterDataTypeDefinition;
}

/**
 * Keep in sync with the backend node adapter's fieldClientIdentifier format in
 * ParameterSettingToWorkflowFieldDefinitionMapper.resolve_field_client_identifier
 */
function getFieldClientId(fieldGroupKey: ProcedureFieldGroupKey, fieldName: string): string {
  return `${fieldGroupKey}/${fieldName}`;
}

/**
 * Creates a row data object for a field definition.
 *
 * @param field - The field definition from GraphQL
 * @param sectionName - Display name for the section containing this field
 * @param sectionType - Type of section (inputs, outputs, or not mappable)
 * @returns Formatted row data for the task schema node renderer
 */
function createSectionRow(
  field: ProcedureFieldInfo,
  fieldGroupKey: ProcedureFieldGroupKey
): TaskSchemaNodeRowData {
  const {icon, label} = getIconAndLabel({
    field: {
      fieldType: field.dataType.type,
      requiredLink: field.dataType.requiredLink,
      unitSymbol: field.dataType.unit?.symbol ?? null,
      unitApiIdentifier: field.dataType.unit?.id ?? null,
    },
    schemaFieldSelectors: [],
  });
  const fieldClientId = getFieldClientId(fieldGroupKey, field.name);
  const {sectionName, sectionType} = PROCEDURE_SECTION_CONFIGURATION_BY_FIELD_GROUP_KEY[fieldGroupKey];
  return {
    id: uuidv4(),
    clientId: fieldClientId,
    fieldName: field.name,
    fieldIcon: icon,
    fieldTypeLabel: label,
    sectionName,
    sectionType,
    isFlowchartTask: false,
  };
}

/**
 * Gets fields for a specific procedure field group from the task definition version.
 * Material outputs use outputFieldDefinitions, all others use inputFieldDefinitions.
 */
function getFieldsForGroup(
  methodDefinitionVersion: MethodNode,
  fieldGroupKey: ProcedureFieldGroupKey
): ReadonlyArray<ProcedureFieldInfo> {
  switch (fieldGroupKey) {
    case ProcedureFieldGroupKey.MATERIAL_OUTPUTS:
      return methodDefinitionVersion.materialOutputSettings.map(
        ({materialOutputVersion}) => materialOutputVersion
      );
    case ProcedureFieldGroupKey.MATERIAL_INPUTS:
      return methodDefinitionVersion.materialInputSettings.map(
        ({materialInputVersion}) => materialInputVersion
      );
    case ProcedureFieldGroupKey.DATA_PARAMETERS:
      return methodDefinitionVersion.dataParameterSettings.map(
        ({dataParameterVersion}) => dataParameterVersion
      );
    case ProcedureFieldGroupKey.EQUIPMENT:
      return methodDefinitionVersion.equipmentSettings.map(({equipmentVersion}) => equipmentVersion);
    default:
      assert(false, `Unknown field group key: ${assertNever(fieldGroupKey)}`);
      return [];
  }
}

/**
 * Adapter class for rendering procedure method nodes in the Process Flowchart Editor.
 * This adapter specializes the generic ProcessFlowchartTaskSchemaNodeAdapter for procedure-specific
 * rendering and behavior, handling procedure method definition versions and their
 * associated field groupings (materials, parameters, equipment).
 */
export default class ProcedureMethodNodeAdapter extends ProcessFlowchartTaskSchemaNodeAdapter<
  MethodNode,
  MethodNodeREST
> {
  readonly executionType: ProcessExecutionType = ProcessExecutionType.PROCEDURE_METHOD;

  readonly typeDisplayName: string = 'unit operation';

  nodeDetailsSidepanelContent: Array<SidepanelTab> = [
    {
      key: NodeSettingsTabKeys.TASK_SCHEMA_DETAILS,
      label: 'Method',
      content: MethodSidepanelEditor,
    },
  ];

  convertTaskDefinitionVersionGQLToApplicationData(
    taskDefinitionVersionGQL: ProcessTaskDefinitionVersionGraphQL
  ): MethodNode {
    const applicationData = taskDefinitionVersionGQL.applicationData;
    assert(applicationData != null, 'Application data is required');
    assert(
      applicationData.__typename === 'ProcedureMethodDefinitionVersionNode',
      'Application data is not a method node'
    );
    return parseMethodNodeFromNetwork(applicationData, (id, errorContext) => id);
  }

  convertTaskDefinitionVersionSpecToNetworkApplicationData(
    taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema<MethodNode>,
    flowchartConfigId: string
  ): MethodNodeREST {
    assert(
      taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.applicationData != null,
      'Application data is required'
    );
    return serializeMethodNodeForNetwork(
      taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.applicationData,
      undefined,
      flowchartConfigId
    );
  }

  /**
   * Extracts the display name for a procedure node from its task definition version.
   * Expects the application data to be a Procedure method definition version.
   *
   * @param taskDefinitionVersion - The task definition version to extract name from
   * @returns The display name for the procedure node
   * @throws Error if applicationData is null or not a ProcedureMethodDefinitionVersionNode
   */
  getNodeName(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<MethodNode>,
    isFlowchartOutput?: boolean,
    existingNodeNames?: Array<string>
  ): string {
    const applicationData = taskDefinitionVersion.newProcessTaskDefinitionVersion.applicationData;

    assert(
      applicationData != null,
      `applicationData is required for task definition version ${taskDefinitionVersion.clientId}`
    );
    return applicationData.name;
  }

  getNodeSubtitle(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<MethodNode>,
    isFlowchartOutput?: boolean
  ): string | null {
    return null;
  }

  getNodeIcon(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<MethodNode>,
    isFlowchartOutput?: boolean
  ): BenchlingIconName | null {
    return null;
  }

  /**
   * Creates section data for rendering the procedure node's field groups.
   * Organizes input fields and output fields into logical sections (materials, parameters, equipment)
   * based on their field group keys.
   *
   * @param taskDefinitionVersion - The task definition version containing field definitions
   * @param nodeDetailsType - The type of node details (unused in this implementation)
   * @returns Array of section data for the task schema node renderer
   */
  createSections(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<MethodNode>,
    nodeDetailsType: ProcessFlowchartNodeDetailsType
  ): Array<TaskSchemaNodeSectionData> {
    const applicationData = taskDefinitionVersion.newProcessTaskDefinitionVersion.applicationData;
    if (applicationData == null) {
      return [];
    }

    return Object.values(ProcedureFieldGroupKey).map((fieldGroupKey) => {
      const {sectionName, sectionType} = PROCEDURE_SECTION_CONFIGURATION_BY_FIELD_GROUP_KEY[fieldGroupKey];
      const methodFields = getFieldsForGroup(applicationData, fieldGroupKey);
      return {
        sectionName,
        sectionType,
        sectionRows: methodFields.map((field) => createSectionRow(field, fieldGroupKey)),
        hideSection: false,
      };
    });
  }

  onCreateNewNode(
    context: NodeCreationContext
  ): Promise<TaskDefinitionVersionSpecWithTaskSchema<MethodNode> | null> {
    assert(context.singletonTaskSchemaForExecutionType != null, 'Singleton task schema is required');

    const newMethodCid = uuidv4();
    const newMethodNode: MethodNode = makeNewEmptyMethodNode(
      context.rootTaskSchema.clientOrigin === ProcessClientOrigin.RECIPES
        ? ProcedureType.RECIPE
        : ProcedureType.ASSAY,
      0,
      0,
      newMethodCid
    );
    newMethodNode.name = getIncrementedNodeName('New unit operation', context.existingNodeNames);
    const newTaskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema<MethodNode> = {
      clientId: uuidv4(),
      inputFieldDefinitions: [],
      outputFieldDefinitions: null,
      newProcessTaskDefinitionVersion: {
        applicationData: newMethodNode,
        taskSchemaId: context.singletonTaskSchemaForExecutionType.id,
        taskSchema: context.singletonTaskSchemaForExecutionType,
      },
      currentVersionId: null,
    };
    return Promise.resolve(newTaskDefinitionVersionSpec);
  }

  getNewNodeDetails(context: NodeCreationContext): {
    nodeName: string;
    nodeSubtitle: string | null;
    nodeIcon: BenchlingIconName | null;
  } {
    return {
      nodeName: 'New unit operation',
      nodeSubtitle: null,
      nodeIcon: null,
    };
  }

  getSingletonTaskSchemaName(
    rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL
  ): ProcessSingletonTaskSchemaName | null {
    if (rootTaskSchema.clientOrigin === ProcessClientOrigin.RECIPES) {
      return ProcessSingletonTaskSchemaName.RECIPE_UNIT_OPERATION_TASK_SCHEMA_NAME;
    } else {
      return ProcessSingletonTaskSchemaName.ASSAY_UNIT_OPERATION_TASK_SCHEMA_NAME;
    }
  }

  override getDisplayOptions(): TaskSchemaDisplayOptions {
    return {rightSidepanelSize: SidepanelSize.FULL};
  }
}
